import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const ACCESS_KEY = 'ahd_access_token';
const REFRESH_KEY = 'ahd_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15000,
});

// Attach Bearer on every request
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// On 401, try refresh once; if refresh also fails, sign out.
let refreshing: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const { data } = await axios.post('/api/auth/refresh', { refresh_token: refresh });
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    clearTokens();
    return null;
  }
}

function friendlyMessage(status: number | undefined, url: string | undefined, serverMessage: string | undefined): string | undefined {
  if (status === 403) {
    if (url?.startsWith('/users') && (url === '/users' || /^\/users\/[^/]+\/(status|reset-password)$/.test(url))) {
      return 'You are not authorised to manage users. Please contact a System Administrator.';
    }
    return 'You are not authorised to perform this action.';
  }
  return serverMessage;
}

http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const original = error.config;

    if (status === 401 && !original._retry && original.url !== '/auth/login') {
      original._retry = true;
      refreshing = refreshing ?? attemptRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      }
      // Refresh failed — kick to login
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    const serverMessage = error?.response?.data?.error?.message;
    const friendly = friendlyMessage(status, original?.url, serverMessage);
    if (friendly) error.message = friendly;
    return Promise.reject(error);
  },
);
